import $clientApi from '@/api/client'
import type { ApiError, ApiResponse } from '@/api/types'
import type {
  QrMenuAddCardResponse,
  QrMenuCardInfoReview,
  QrMenuConfirmedCard,
  QrMenuPaymentSystem,
} from '@/composables/qr-menu/types'
import { useAppToast } from '@/utils/toast'
import { useLocalStorage } from '@vueuse/core'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export const useQrMenuPaymentStore = defineStore('qr-menu-payment', () => {
  const { showErrorToast, showSuccessToast } = useAppToast()
  const { t } = useI18n()
  const paymentSystemList = ref<QrMenuPaymentSystem[]>([])

  const selectedCard = useLocalStorage<string>('selectedCard', '')
  const cardList = useLocalStorage<QrMenuConfirmedCard[]>('cardList', [])

  const cardEntry = reactive({
    number: '',
    expiry: '',
    cvc: '',
    ps: ''
  })

  const bindCard = reactive({
    otpModalVisible: false,
    otpPendingToken: '',
    otp: '',
    otpConfirmLoading: false,
    addFormVisible: false,
    addSubmitLoading: false
  })

  const menuCheckout = reactive({
    paymentSystem: undefined as string | undefined,
    pendingCardToken: null as string | null
  })

  const setPendingCardTokenForOrder = (token: string | null) => {
    menuCheckout.pendingCardToken = token?.trim() || null
  }

  const clearPendingCardTokenForOrder = () => {
    menuCheckout.pendingCardToken = null
  }

  const setPaymentSystem = (system: string) => {
    menuCheckout.paymentSystem = system
  }

  const clearPaymentSystemSelection = () => {
    menuCheckout.paymentSystem = undefined
  }

  const resetPaymentForm = () => {
    cardEntry.number = ''
    cardEntry.expiry = ''
    cardEntry.cvc = ''
    cardEntry.ps = ''
    menuCheckout.paymentSystem = undefined
    clearPendingCardTokenForOrder()
  }

  const closeCardOtpModal = () => {
    bindCard.otpModalVisible = false
    bindCard.otp = ''
    bindCard.otpPendingToken = ''
  }

  const openCardAddFormModal = () => {
    cardEntry.number = ''
    cardEntry.expiry = ''
    cardEntry.cvc = ''
    cardEntry.ps = ''
    bindCard.addFormVisible = true
  }

  const closeCardAddFormModal = () => {
    bindCard.addFormVisible = false
  }

  const canSubmitAddCard = computed(() => {
    const pan = cardEntry.number.replace(/\D/g, '')
    const exp = cardEntry.expiry.replace(/\D/g, '')
    return pan.length >= 16 && exp.length === 4
  })

  const addCardPayload = computed(() => ({
    pan: cardEntry.number,
    expiry: cardEntry.expiry,
    ps: cardEntry.ps,
    recaptcha: ''
  }))

  const checkPs = async () => {
    const digits = cardEntry.number.replace(/\D/g, '')
    if (digits.length < 4 || digits.length > 6) {
      return
    }
    const bin = digits.slice(0, 6)
    try {
      const res = await $clientApi.get<ApiResponse<QrMenuCardInfoReview>>('/review/card-info', {
        params: { bin }
      })
      const row = res.data?.data
      if (row?.ps != null && String(row.ps).trim() !== '') cardEntry.ps = String(row.ps).trim()
    } catch (error) {
      console.error(error)
      cardEntry.ps = ''
    }
  }

  const addCard = async () => {
    if (bindCard.addSubmitLoading) return
    bindCard.addSubmitLoading = true
    addCardPayload.value.recaptcha = await window.grecaptcha.execute('6LdSo3krAAAAAHIFJQO-0iveC6pQTrJc5B6mRKvT', { action: 'add_card' })
    try {
      const res = await $clientApi.post<ApiResponse<QrMenuAddCardResponse>>(
        '/payment/card',
        addCardPayload.value
      )
      const body = res.data
      const data = body?.data
      if (body?.success && data?.card_token) {
        bindCard.addFormVisible = false
        selectedCard.value = data.card_token
        bindCard.otpPendingToken = data.card_token
        bindCard.otp = ''
        bindCard.otpModalVisible = true
      }
      return data
    } catch (error) {
      console.error(error)
      const err = error as AxiosError<ApiError>
      showErrorToast(
        t('toast.error'),
        err.response?.data?.error?.details ??
        err.response?.data?.error?.message ??
        t('qrMenu.cartPayment.addCardFailed')
      )
      throw error
    } finally {
      bindCard.addSubmitLoading = false
    }
  }

  const confirmCardOtp = async () => {
    const otp = bindCard.otp.replace(/\D/g, '')
    if (otp.length !== 6) return
    const token = bindCard.otpPendingToken
    if (!token) return
    const captcha = await window.grecaptcha.execute('6LdSo3krAAAAAHIFJQO-0iveC6pQTrJc5B6mRKvT', { action: 'confirm_card_otp' })
    bindCard.otpConfirmLoading = true
    try {
      const res = await $clientApi.put<ApiResponse<QrMenuConfirmedCard>>(
        `/payment/card/${encodeURIComponent(token)}`,
        { otp, recaptcha: captcha },
        {
          params: {
            selected_card: selectedCard.value || token
          }
        }
      )
      const row = res.data?.data
      if (row?.token) {
        selectedCard.value = row.token
        const rowPan = (row.pan ?? '').trim()
        const next =
          rowPan.length > 0
            ? cardList.value.filter(c => (c.pan ?? '').trim() !== rowPan)
            : [...cardList.value]
        next.push(row)
        cardList.value = next
      }
      showSuccessToast(t('toast.success'), t('qrMenu.orderPaymentOtp.cardConfirmed'))
      closeCardOtpModal()
    } catch (error) {
      console.error(error)
      const err = error as AxiosError<ApiError>
      showErrorToast(
        t('toast.error'),
        err.response?.data?.error?.details ??
        err.response?.data?.error?.message ??
        t('qrMenu.orderPaymentOtp.confirmFailed')
      )
    } finally {
      bindCard.otpConfirmLoading = false
    }
  }

  const fetchPaymentSystems = async (): Promise<QrMenuPaymentSystem[]> => {
    try {
      const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
      const res = await $clientApi.get<ApiResponse<QrMenuPaymentSystem[]>>('/menu/profile/payment-systems', {
        baseURL: baseUrl
      })
      const list = Array.isArray(res.data?.data) ? res.data.data : []
      paymentSystemList.value = list
      return list
    } catch (error) {
      console.error(error)
      paymentSystemList.value = []
      return []
    }
  }

  return {
    paymentSystemList,
    cardEntry,
    bindCard,
    menuCheckout,
    selectedCard,
    cardList,
    canSubmitAddCard,
    addCardPayload,
    setPendingCardTokenForOrder,
    clearPendingCardTokenForOrder,
    setPaymentSystem,
    clearPaymentSystemSelection,
    resetPaymentForm,
    closeCardOtpModal,
    openCardAddFormModal,
    closeCardAddFormModal,
    fetchPaymentSystems,
    addCard,
    checkPs,
    confirmCardOtp
  }
})
